/**
 * Holding on to the browser's own install prompt, and reading the facts the route needs.
 *
 * The browser half of F-121. `route.ts` holds the decision and knows nothing about a browser;
 * this knows about the browser and decides nothing.
 *
 * **`beforeinstallprompt` fires once, early, and is lost if nobody is listening.** Chromium
 * dispatches it when it first decides the site is installable — which on a warm load is before
 * React has mounted anything — and an unhandled event is simply gone, taking the only one-tap
 * install we have with it. So the listener is attached at module load from `main.tsx`, beside
 * `registerWorker`, for the same reason that one is: both have to happen before the application
 * exists rather than inside the screen that eventually needs them.
 */
import { isSafari, type InstallFacts } from './route.js';

/** The event Chromium fires, which TypeScript's DOM library does not describe. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | undefined;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // A subscriber that cannot re-render is not a reason to lose the prompt for the others.
    }
  }
}

/**
 * Starts listening. Safe to call more than once; only the first call attaches anything.
 *
 * Also listens for `appinstalled`, which is what tells us the offer has been taken — on a
 * desktop the app opens in a new window and the tab that made the offer stays exactly where it
 * was, still showing a button to install something that is now installed.
 */
let watching = false;
export function watchForInstallPrompt(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    /*
     * Held rather than allowed to proceed. Without `preventDefault` Chromium shows its own
     * mini-infobar, which is the browser deciding when to interrupt somebody — and then the
     * event cannot be replayed from a button later, so the offer exists only in the moment the
     * browser chose it.
     */
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    announce();
  });

  window.addEventListener('appinstalled', () => {
    // Spent. The same event object cannot be prompted twice, and an offer that is still on
    // screen after the app has been installed is the clearest possible sign nothing is
    // listening to the browser.
    deferred = undefined;
    announce();
  });
}

/** Re-renders a caller when the offer appears or is taken. Returns the unsubscribe. */
export function onInstallOfferChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Shows the browser's own install dialog, and answers whether it was accepted.
 *
 * The event is dropped either way. Chromium refuses a second `prompt()` on the same event, so
 * keeping a dismissed one would leave a button that silently does nothing from its second tap
 * onwards — the failure this whole feature exists to remove. A learner who changes their mind
 * gets a fresh event on their next visit, which is the browser's own rule about how often it is
 * willing to ask.
 */
export async function showInstallPrompt(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  deferred = undefined;
  try {
    await event.prompt();
    const choice = await event.userChoice;
    announce();
    return choice.outcome === 'accepted';
  } catch {
    announce();
    return false;
  }
}

/** Everything `installRoute` needs, read from this browser. */
export function installFacts(): InstallFacts {
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return {
    standalone: legacy || window.matchMedia('(display-mode: standalone)').matches,
    apple:
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
    safari: isSafari(navigator.userAgent),
    deferred: deferred !== undefined,
  };
}
